import React, { useState, useEffect } from 'react';
import '../../App.css';
import axios from 'axios';
import {
  Grid, TextField, Box, Button, Paper, Stack, FormControl, FormLabel, RadioGroup,
  FormControlLabel, Radio, Typography
} from '@mui/material';
import { HiOutlineCurrencyRupee } from "react-icons/hi2";
import { PriceChange } from '@mui/icons-material';
import EarningDeductionTable from '../table-data/EarningDeductionTable';
import { useNavigate } from 'react-router-dom';
import Notification from '../Notification';

// Custom component for required field label
const RequiredLabel = ({ label }) => (
  <span>
    {label}
    <span style={{ color: 'red', marginLeft: '3px' }}>*</span>
  </span>
);

const initialFValues = {
  id: 0,
  deductionOrEarningType: '',
  type: '',
};

function EarningAndDeductionMaster() {
  const [values, setValues] = useState(initialFValues);
  const [errors, setErrors] = useState({});
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const [tableData, setTableData] = useState([]);
  const navigate = useNavigate();

  const validate = () => {
    let temp = {};
    let isValid = true;

    // Basic validation for required fields
    if (!values.deductionOrEarningType) {
      temp.deductionOrEarningType = 'This field is required.';
      isValid = false;
    }

    if (!values.type) {
      temp.type = 'Please select a type.';
      isValid = false;
    }

    // Check for an exact duplicate (same name and type)
    if (values.deductionOrEarningType && values.type) {
      const exactDuplicate = tableData.find(
        item => item.component.toLowerCase() === values.deductionOrEarningType.toLowerCase() &&
          item.componentType === values.type
      );

      if (exactDuplicate) {
        temp.deductionOrEarningType = 'Duplicate entry: same name and type already exists.';
        isValid = false;
      }
    }

    // Check for a conflicting type (same name, different type)
    if (isValid && values.deductionOrEarningType && values.type) {
      const conflictingType = tableData.find(
        item => item.component.toLowerCase() === values.deductionOrEarningType.toLowerCase() &&
          item.componentType !== values.type
      );

      if (conflictingType) {
        temp.deductionOrEarningType = `Name already exists with a ${conflictingType.componentType} type.`;
        isValid = false;
      }
    }

    setErrors({ ...temp });
    return isValid;
  };

  const resetForm = () => {
    setValues(initialFValues);
    setErrors({});
  };

  const fetchTableData = () => {
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/employee-master/get-earning-deductions`)
      .then(response => {
        setTableData(response.data.map(item => ({
          id: item.deduction_earning_id,
          component: item.deduction_earning_type,
          componentType: item.earning === 1 ? 'earning' : 'deduction'
        })));
      })
      .catch(error => {
        console.error("Error fetching table data: ", error);
        if (error.response?.status === 401) {
          setNotify({ isOpen: true, message: 'Session Expired!', type: 'error' });
          setTimeout(() => navigate('/'), 1000);
        }
      });
  };

  useEffect(() => {
    fetchTableData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
    // Clear validation error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      axios.post(`${process.env.REACT_APP_API_BASE_URL}/employee-master/add-earning-deduction`, values)
        .then(function (response) {
          if (response.status === 200) {
            setNotify({
              isOpen: true,
              message: 'Entry added successfully!',
              type: 'success',
            });
            fetchTableData();
            resetForm();
          }
        })
        .catch(function (e) {
          // Keep API error handling for other issues like session expiration or server errors.
          if (e.response && e.response.status === 401) {
            setNotify({ isOpen: true, message: 'Session Expired!', type: 'error' });
            setTimeout(() => navigate('/'), 1000);
          } else {
            setNotify({ isOpen: true, message: 'Error saving data. Please try again.', type: 'error' });
          }
        });
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', margin: '10px', padding: '50px', fontWeight: '500px' }}>
      <Paper elevation={5}>
        <Grid className="form-title" container style={{ marginBottom: '-20px' }} spacing={2} justifyContent="center" alignItems="center">
          <Grid item>
            <HiOutlineCurrencyRupee size={40} style={{ marginLeft: "15px" }} sx={{ width: 45, height: 45 }} />
          </Grid>
          <Grid item>
            <Typography variant="h5" sx={{ textAlign: 'center', margin: 5, marginLeft: '-10px' }}>
              EARNING AND DEDUCTION MASTER
            </Typography>
          </Grid>
        </Grid>

        <form className="classes.root" autoComplete="off" onSubmit={handleSubmit}>
          <Stack spacing={2} sx={{ margin: '30px', paddingBottom: '0px' }}>
            <Stack className="center-aligned">
              <EarningDeductionTable data={tableData} />
            </Stack>

            <TextField
              variant="outlined"
              name="deductionOrEarningType"
              id="outlined-basic"
              label={<RequiredLabel label="Deduction Or Earning Type" />}
              size="small"
              value={values.deductionOrEarningType}
              onChange={handleInputChange}
              {...(errors.deductionOrEarningType && { error: true, helperText: errors.deductionOrEarningType })}
            />

            <FormControl component="fieldset" sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <FormLabel sx={{ mr: 2 }}>
                <RequiredLabel label="Type" />
              </FormLabel>
              <RadioGroup row name="type" value={values.type} onChange={handleInputChange}>
                <FormControlLabel value="deduction" control={<Radio />} label="Deduction" />
                <FormControlLabel value="earning" control={<Radio />} label="Earning" />
              </RadioGroup>
              {errors.type && <Typography color="error" variant="caption">{errors.type}</Typography>}
            </FormControl>

            <Grid container className="form-btn-container" justifyContent="center" spacing={2}>
              <Grid item>
                <Button className="form-btn" variant="outlined" color="secondary" onClick={resetForm}>
                  Reset
                </Button>
              </Grid>
              <Grid item>
                <Button className="form-btn" variant="contained" type="submit">
                  Save
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </form>
      </Paper>
      <Notification notify={notify} setNotify={setNotify} />
    </Box>
  );
}

export default EarningAndDeductionMaster;
